import axios from 'axios';
import { getApiUrl } from '../../../config/api';

export class AddMenuController {
  constructor() {
    this.menu = {
      Nombre: '',
      MenuPadre: null,
      Orden: 0
    };
    this.parentMenus = [];
    this.saving = false;
    this.loading = false;
    this.error = null;
  }

  /**
   * Inicializa el controlador cargando los menús padre disponibles
   */
  async init() {
    await this.fetchParentMenus();
  }

  /**
   * Resetea el formulario a su estado inicial
   */
  resetForm() {
    this.menu = {
      Nombre: '',
      MenuPadre: null,
      Orden: 0
    };
    this.error = null;
  }

  /**
   * Obtiene la lista de menús padre disponibles
   */
  async fetchParentMenus() {
    this.loading = true;
    this.error = null;

    try {
      const response = await axios.post(getApiUrl('/api/Menu/listar'));
      this.parentMenus = response.data || [];
      this.loading = false;
    } catch (error) {
      console.error('Error al cargar menús padre:', error);
      this.error = 'No se pudieron cargar los menús padre. Por favor, intente nuevamente.';
      this.loading = false;
      throw error;
    }
  }

  /**
   * Valida los datos del menú antes de guardar
   * @returns {boolean} true si los datos son válidos
   */
  validateMenu() {
    if (!this.menu.Nombre || this.menu.Nombre.trim() === '') {
      this.error = 'El nombre del menú es requerido';
      return false;
    }

    if (this.menu.Nombre.length > 100) {
      this.error = 'El nombre del menú no puede exceder 100 caracteres';
      return false;
    }

    if (this.menu.Orden < 0) {
      this.error = 'El orden debe ser un número positivo';
      return false;
    }

    if (this.menu.Orden > 9999) {
      this.error = 'El orden no puede ser mayor a 9999';
      return false;
    }

    this.error = null;
    return true;
  }

  /**
   * Prepara los datos del menú antes del envío
   */
  prepareMenuData() {
    // Limpiar espacios en blanco del nombre
    this.menu.Nombre = this.menu.Nombre.trim();
    
    // Asegurar que el orden sea un número
    this.menu.Orden = parseInt(this.menu.Orden) || 0;
    
    // Si MenuPadre es null o vacío, asegurar que sea null
    if (!this.menu.MenuPadre) {
      this.menu.MenuPadre = null;
    }
  }

  /**
   * Guarda el menú en el servidor
   * @returns {Promise} Promise que resuelve cuando el menú es guardado
   */
  async saveMenu() {
    if (!this.validateMenu()) {
      return Promise.reject(new Error(this.error));
    }

    this.saving = true;
    this.error = null;

    try {
      this.prepareMenuData();
      
      const response = await axios.post(getApiUrl('/api/Menu/guardar'), this.menu);
      
      this.saving = false;
      
      // Verificar si la respuesta indica éxito
      const success = response.data && (response.data.Number > 0 || response.data.success !== false);
      
      return {
        success: success,
        message: response?.data?.message || 'Menú guardado correctamente.',
        data: response.data
      };
    } catch (error) {
      console.error('Error al guardar menú:', error);
      this.error = 'No se pudo guardar el menú. Por favor, verifique los datos e intente nuevamente.';
      this.saving = false;
      throw error;
    }
  }

  /**
   * Obtiene el siguiente orden disponible para un menú padre específico
   * @param {number|null} menuPadreId - ID del menú padre
   * @returns {number} Siguiente orden disponible
   */
  getNextOrder(menuPadreId = null) {
    const siblings = this.parentMenus.filter(menu => menu.MenuPadre === menuPadreId);
    if (siblings.length === 0) {
      return 1;
    }
    const maxOrder = Math.max(...siblings.map(menu => menu.Orden || 0));
    return maxOrder + 1;
  }

  /**
   * Maneja el cambio del menú padre y ajusta el orden automáticamente
   * @param {number|null} menuPadreId - ID del nuevo menú padre
   */
  handleMenuPadreChange(menuPadreId) {
    this.menu.MenuPadre = menuPadreId;
    // Sugerir el siguiente orden disponible
    this.menu.Orden = this.getNextOrder(menuPadreId);
    
    // Limpiar error cuando el usuario modifica los datos
    if (this.error) {
      this.error = null;
    }
  }

  /**
   * Actualiza un campo específico del menú
   * @param {string} field - Nombre del campo
   * @param {any} value - Valor del campo
   */
  updateField(field, value) {
    if (Object.prototype.hasOwnProperty.call(this.menu, field)) {
      this.menu[field] = value;
      
      // Limpiar error cuando el usuario modifica los datos
      if (this.error) {
        this.error = null;
      }
    }
  }

  /**
   * Obtiene el estado actual del menú
   * @returns {Object} Copia del objeto menú
   */
  getMenu() {
    return { ...this.menu };
  }

  /**
   * Obtiene la lista de menús padre
   * @returns {Array} Lista de menús padre
   */
  getParentMenus() {
    return [...this.parentMenus];
  }

  /**
   * Obtiene el estado de guardado
   * @returns {boolean} true si está guardando
   */
  isSaving() {
    return this.saving;
  }

  /**
   * Obtiene el estado de carga
   * @returns {boolean} true si está cargando
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Obtiene el error actual
   * @returns {string|null} Mensaje de error o null
   */
  getError() {
    return this.error;
  }

  /**
   * Limpia el error actual
   */
  clearError() {
    this.error = null;
  }
}

// Exportar una instancia por defecto para uso directo
export default new AddMenuController();